<template>
  <div class="consumable-details" :class="{ 'consumable-details--modal': isInModalMode }">
    <b-loading :active="isLoading" :is-full-page="false"></b-loading>

    <OmxPageHeader
        :title="headerTitle"
        :subtitle="headerSubtitle"
        :pills="headerPills"
        :sticky="!isInModalMode"
    >
      <template #actions>
        <template v-if="isEditMode">
          <b-button @click="cancelClicked" icon-left="close-thick" tabindex="31">{{ $t('common.cancel') }}</b-button>
          <OmxPermissionButton :allowed="canSave" type="is-primary" @click="saveClicked" icon-left="content-save" tabindex="32">{{ $t('common.save') }}</OmxPermissionButton>
        </template>
        <template v-else>
          <b-button v-if="!isInModalMode" @click="backClicked" icon-left="arrow-left">{{ $t('common.back') }}</b-button>
          <OmxPermissionButton :allowed="canEdit" type="is-primary" @click="editClicked" icon-left="pencil">{{ $t('common.edit') }}</OmxPermissionButton>
          <OmxPermissionButton :allowed="canDelete" type="is-danger" @click="deleteClicked" icon-left="delete">{{ $t('common.delete') }}</OmxPermissionButton>
        </template>
      </template>
    </OmxPageHeader>

    <OmxCard class="consumable-details__card">
      <div class="columns">
                <div class="column is-half">
                  <OmxTagInput
                      v-model="selectedResources"
                      :label="$t('consumable-details-view.consumable-resource-field')"
                      :data="filteredResources"
                      :loading="isFetchingCategories"
                      :view-only="!isEditMode"
                      :maxtags="1"
                      :allow-new="true"
                      :placeholder="$t('consumable-details-view.consumable-resource-placeholder')"
                      tabindex="11"
                      @typing="getFilteredResources"
                      @input="onResourcesInput"
                      @blur="onResourceInputBlur"
                  />
                  <OmxTagInput
                      v-model="selectedCategories"
                      :label="$t('consumable-details-view.consumable-category-field')"
                      :data="filteredCategories"
                      :loading="isFetchingCategories"
                      :view-only="!isEditMode"
                      :maxtags="1"
                      :allow-new="true"
                      :placeholder="$t('consumable-details-view.consumable-category-placeholder')"
                      tabindex="12"
                      @typing="getFilteredCategories"
                      @input="onCategoriesInput"
                      @blur="onCategoryInputBlur"
                  />
                  <OmxTagInput
                      v-model="selectedProviders"
                      :label="$t('consumable-details-view.consumable-provider-field')"
                      :data="filteredProviders"
                      :loading="isFetchingCategories"
                      :view-only="!isEditMode"
                      :maxtags="1"
                      :allow-new="true"
                      :placeholder="$t('consumable-details-view.consumable-provider-placeholder')"
                      tabindex="13"
                      @typing="getFilteredProviders"
                      @input="onProvidersInput"
                      @blur="onProviderInputBlur"
                  />

                  <OmxField
                      v-model="consumableData.text"
                      type="textarea"
                      :label="$t('consumable-details-view.consumable-text-field')"
                      :placeholder="$t('consumable-details-view.consumable-text-placeholder')"
                      :view-only="!isEditMode"
                      tabindex="14"
                  />
                </div>

                <div class="column">
                  <div class="columns no-margin" v-if="isEditMode">
                    <div class="column">
                      <OmxField
                          v-model="consumableData.meta.estimation.quantity"
                          type="number"
                          min="0"
                          step="any"
                          :label="$t('consumable-details-view.consumable-quantity-field')"
                          :placeholder="$t('consumable-details-view.consumable-quantity-placeholder')"
                          tabindex="16"
                      />
                    </div>
                    <div class="column">
                      <OmxField
                          v-model="consumableData.unit"
                          type="select"
                          :label="$t('consumable-details-view.consumable-quantityunit-placeholder')"
                          :placeholder="$t('consumable-details-view.consumable-quantityunit-placeholder')"
                          :options="quantityUnitOptions"
                          tabindex="17"
                      />
                    </div>
                  </div>
                  <OmxField
                      v-else
                      :label="$t('consumable-details-view.consumable-quantity-field')"
                      view-only
                  ><template #value>{{ getFormattedQuantity(consumableData.meta.estimation, consumableData.unit) }}</template></OmxField>

                  <OmxField
                      v-if="isEditMode"
                      v-model="consumableData.meta.estimation.fee_per_unit"
                      type="number"
                      min="0"
                      step="any"
                      :label="$t('consumable-details-view.consumable-fee-per-unit-field')"
                      :placeholder="$t('consumable-details-view.consumable-fee-per-unit-placeholder')"
                      :hint="defaultValues.priceUnit"
                      tabindex="18"
                  />
                  <OmxField
                      v-else
                      :label="$t('consumable-details-view.consumable-fee-per-unit-field')"
                      view-only
                  ><template #value>{{ getFormattedFeePerUnit(consumableData.meta.estimation) }}</template></OmxField>

                  <p v-if="isEditMode && invalidEstimation" class="consumable-details__validation">
                    <b-icon icon="alert-circle-outline" size="is-small" />
                    {{ $t('validation.equipment-details') }}
                  </p>
                </div>
              </div>
    </OmxCard>
  </div>
</template>

<script>
import firebase from "firebase/app";
import OmxPageHeader from "@/components/OmxPageHeader.vue";
import OmxCard from "@/components/OmxCard.vue";
import OmxField from "@/components/OmxField.vue";
import OmxTagInput from "@/components/OmxTagInput.vue";

export default {
  name: "ConsumableDetails",
  components: { OmxPageHeader, OmxCard, OmxField, OmxTagInput },
  props: {
    'project_id': {
      type: String,
    },
    'consumable_id': {
      type: String,
    },
    'is_template': {
      type: Boolean,
    }
  },
  data() {
    return {
      consumableData: {
        meta: {
          estimation: {
            quantity: null,
            fee_per_unit: null
          }
        },
        unit: ""
      },
      isLoading: false,
      isEditMode: false,
      invalidEstimation: false,
      isFetchingCategories: false,
      categoricalData: {},
      filteredResources: [],
      selectedResources: [],
      filteredCategories: [],
      selectedCategories: [],
      filteredProviders: [],
      selectedProviders: []
    }
  },
  methods: {
    readConsumableData: function () {
      this.isLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getConsumableFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getConsumableFunction({
        action: "get_consumable_by_id",
        project_id: this.project_id,
        document_id: this.consumable_id,
        fill_meta: true
      }).then((result) => {
        this.isLoading = false;
        this.consumableData = result.data["consumables"];
        if (!this.consumableData.meta.estimation)
          this.$set(this.consumableData.meta, 'estimation', {});
        const resource = this.consumableData.resource
        if (resource !== undefined && resource !== "") {
          this.selectedResources[0] = resource
        }
        const category = this.consumableData.category
        if (category !== undefined && category !== "") {
          this.selectedCategories[0] = category
        }
        const provider = this.consumableData.provider
        if (provider !== undefined && provider !== "") {
          this.selectedProviders[0] = provider
        }
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error getting consumables:", error?.code);
      });
    },
    createConsumable: function (updateData) {
      this.isLoading = true
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const insertConsumableFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "insert_consumable";
      insertConsumableFunction(updateData).then(() => {
        this.isLoading = false
        this.showSuccess(this.$t('messages.consumable-created'));
        this.$emit('consumableAdded');
      }).catch((error) => {
        this.isLoading = false
        this.showError(this.$t('messages.save-error'))
        console.error("Error creating consumable:", error?.code);
      });
    },
    updateConsumable: function (updateData) {
      this.isLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const updateConsumableFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData.action = "update_consumable";
      updateConsumableFunction(updateData).then(() => {
        this.isLoading = false;
        this.isEditMode = false;
        this.showSuccess(this.$t('messages.consumable-updated'));
      }).catch((error) => {
        this.isLoading = false;
        this.showError(this.$t('messages.save-error'))
        console.error("Error updating consumable:", error?.code);
      });
    },
    deleteConsumable: function () {
      this.isLoading = true;
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const deleteConsumableFunction = functions.httpsCallable(this.getSubmitEndpoint());
      deleteConsumableFunction({
        action: "delete_consumable",
        project_id: this.project_id,
        document_id: this.consumable_id,
      }).then(() => {
        this.isLoading = false;
        this.isEditMode = false;
        this.showSuccess(this.$t('messages.consumable-deleted'));
        this.$emit('consumableDeleted');
        this.$router.back();
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error deleting consumable:", error?.code);
      });
    },
    readCategoricalData: function () {
      this.isFetchingCategories = true
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const getCategoriesFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getCategoriesFunction({
        action: "get_categories",
        collections: ["consumables"]
      }).then((result) => {
        this.isFetchingCategories = false
        this.categoricalData = result.data["consumables"]
        this.filteredResources = this.categoricalData.resource
        this.filteredCategories = this.categoricalData.category
        this.filteredProviders = this.categoricalData.provider
      }).catch((error) => {
        this.isFetchingCategories = false
        this.showError(error.message)
        console.log("Error getting categories:", error?.code);
      });
    },
    /*
     UI Events
     */
    editClicked: function () {
      this.isEditMode = !this.isEditMode;
    },
    async deleteClicked() {
      const ok = await this.$omxConfirm({
        title: this.$t('consumable-details-view.delete-consumable-confirm-title').toString(),
        message: this.$t('consumable-details-view.delete-consumable-confirm-message').toString(),
        confirmText: this.$t('consumable-details-view.delete-consumable-confirm-button').toString(),
        cancelText: this.$t('common.cancel').toString(),
        type: 'is-danger',
        icon: 'delete'
      });
      if (ok) this.deleteConsumable();
    },
    cancelClicked: function () {
      if (this.isInNewMode) {
        this.$emit('close');
      } else if (this.isEditMode) {
        this.readConsumableData();
        this.isEditMode = false;
      }
    },
    backClicked: function () {
      this.$router.back()
    },
    saveClicked: function () {
      // Required fields must be present before we attempt to save.
      const resource = (this.selectedResources[0] || "").toString().trim();
      if (!resource) {
        this.showError(this.$t('messages.required-fields-error'));
        return;
      }

      const dataFieldsToUpdate = {
        project_id: this.project_id,
        text: this.consumableData.text,
        estimation: {}
      }
      if (this.selectedResources[0] !== undefined) {
        dataFieldsToUpdate.resource = this.selectedResources[0]
      }
      if (this.selectedCategories[0] !== undefined) {
        dataFieldsToUpdate.category = this.selectedCategories[0]
      }
      if (this.selectedProviders[0] !== undefined) {
        dataFieldsToUpdate.provider = this.selectedProviders[0]
      }
      let estimation = this.consumableData.meta.estimation;
      if (estimation.quantity == null || estimation.quantity === "" || estimation.quantity === undefined) {
        this.invalidEstimation = true;
      } else {
        dataFieldsToUpdate.estimation.quantity = parseFloat(this.consumableData.meta.estimation.quantity);
        if (this.consumableData.unit == null || this.consumableData.unit === "" || this.consumableData.unit === undefined) {
          this.consumableData.unit = this.defaultValues.quantityUnitId;
        }
        dataFieldsToUpdate.unit = this.consumableData.unit;
      }
      if (estimation.fee_per_unit == null || estimation.fee_per_unit === "" || estimation.fee_per_unit === undefined) {
        this.invalidEstimation = true;
      } else {
        dataFieldsToUpdate.estimation.fee_per_unit = parseFloat(this.consumableData.meta.estimation.fee_per_unit);
      }

      if (this.isInNewMode) {
        this.createConsumable(dataFieldsToUpdate);
      } else {
        dataFieldsToUpdate.document_id = this.consumable_id;
        this.updateConsumable(dataFieldsToUpdate);
      }
    },
    onResourcesInput() {
      this.filteredResources = this.categoricalData.resource
    },
    getFilteredResources(text) {
      this.filteredResources = this.categoricalData.resource.filter((option) => {
        return option
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    onCategoriesInput() {
      this.filteredCategories = this.categoricalData.category
    },
    getFilteredCategories(text) {
      this.filteredCategories = this.categoricalData.category.filter((option) => {
        return option
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    onProvidersInput() {
      this.filteredProviders = this.categoricalData.provider
    },
    getFilteredProviders(text) {
      this.filteredProviders = this.categoricalData.provider.filter((option) => {
        return option
            .toString()
            .toLowerCase()
            .indexOf(text.toLowerCase()) >= 0
      })
    },
    onResourceInputBlur() {
      this.$refs.resourceInput.addTag()
    },
    onCategoryInputBlur() {
      this.$refs.categoryInput.addTag()
    },
    onProviderInputBlur() {
      this.$refs.providerInput.addTag()
    }
  },
  mounted() {
    if ((!this.is_template && !this.currentUserAccess("ConsumableDetails", this.UserActions.VIEW, this.project_id))
        || (this.is_template && !this.currentUserAccess("ProjectTemplates", this.UserActions.VIEW))) {
      console.log("Page access denied.");
      this.$router.replace("/projects/").catch(() => {
      });
    }
    this.readCategoricalData()
    if (this.isInNewMode) {
      console.log("creating new consumable");
      this.isEditMode = true;
    } else {
      this.isEditMode = false;
      this.readConsumableData();
    }
  },
  computed: {
    isInNewMode() {
      return this.consumable_id === "new";
    },
    isInModalMode() {
      return this.$route.name !== 'ConsumableDetails';
    },
    pageTitle() {
      if (this.isInNewMode) {
        return this.$t('consumable-details-view.new-consumable-title')
      } else if (this.isEditMode) {
        return this.$t('consumable-details-view.edit-consumable-title') + " " + this.getAdjustedTitle(this.consumableData.resource)
      } else {
        return this.$t('consumable-details-view.view-consumable-title') + " " + this.getAdjustedTitle(this.consumableData.resource)
      }
    },
    headerTitle() {
      if (this.isInNewMode) return this.$t('consumable-details-view.new-consumable-title');
      return this.getAdjustedTitle(this.selectedResources && this.selectedResources[0]) || this.getAdjustedTitle(this.consumableData.resource);
    },
    headerSubtitle() {
      const parts = [];
      if (this.selectedCategories && this.selectedCategories[0]) parts.push(this.selectedCategories[0]);
      if (this.selectedProviders && this.selectedProviders[0]) parts.push(this.selectedProviders[0]);
      return parts.join(' · ') || null;
    },
    headerPills() {
      return [];
    },
    canEdit() {
      return !this.isEditMode
          && ((!this.is_template && this.currentUserAccess('ConsumableDetails', 'UPDATE', this.project_id))
              || (this.is_template && this.currentUserAccess('ProjectTemplates', 'UPDATE')));
    },
    canSave() {
      return (!this.is_template && this.currentUserAccess('ConsumableDetails', 'UPDATE', this.project_id))
          || (this.is_template && this.currentUserAccess('ProjectTemplates', 'UPDATE'));
    },
    canDelete() {
      return !this.isEditMode && !this.isInNewMode
          && ((!this.is_template && this.currentUserAccess('ConsumableDetails', 'DELETE', this.project_id))
              || (this.is_template && this.currentUserAccess('ProjectTemplates', 'DELETE')));
    },
    quantityUnitOptions() {
      return (this.quantityUnits || []).map(opt => ({
        value: opt.id,
        label: this.$t(opt.translationKey)
      }));
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.consumable-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.consumable-details--modal { padding: 4px; }
.consumable-details__card ::v-deep .columns:last-child { margin-bottom: 0; }
.consumable-details__validation {
  color: $omx-danger-600;
  font-size: 12.5px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 8px 0 0;
}
.estimation-value {
  max-width: 50%;
}

.required-field {
  color: red;
  font-size: 10pt;
}
</style>
